import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DAYS_MAX,
  DAYS_MIN,
  DAYS_STEP,
  DEFAULT_FILTERS,
  applyFilters,
  displayTitle,
  fetchDismissed,
  metaLine,
  summaryLine,
  undismiss,
  type DismissedFilters,
  type DismissedItem,
  type DismissedPayload,
} from "../lib/dismissedPage";
import { formatPacificDate } from "../lib/format";
import { CARD_GRID } from "../lib/cardGrid";

/**
 * Dismissed listings that are still buyable, ported from
 * `server/pages/dismissed.py`.
 *
 * The logic lives in `lib/dismissedPage.ts` and is tested there. What is left
 * here is rendering, the two filter controls, and the four states this page
 * owes the user: loading, error, empty, and the per-card failure of an
 * un-dismiss.
 *
 * The filters run in the browser over the full list, which is why they feel
 * instant and why the `now` they filter against is created once per render
 * rather than per item -- a `new Date()` inside the predicate would make two
 * items on the boundary answer differently within one pass.
 */

function Card({ item, onUndismiss }: { item: DismissedItem; onUndismiss: (key: string) => void }) {
  const meta = metaLine(item);
  const dismissedAt = formatPacificDate(item.dismissed_at);

  return (
    <li className="rounded-md border border-neutral-800 bg-neutral-900 p-3 space-y-2">
      {item.accidental ? (
        <p className="text-xs text-amber-400">⚠️ possibly accidental</p>
      ) : null}

      {item.image_url ? (
        // The image is the link to the listing. `rel` is not optional on a
        // target=_blank link to a marketplace: without `noopener` the opened
        // page gets a handle on this one through `window.opener`.
        <a href={item.url || undefined} target="_blank" rel="noopener noreferrer">
          <img
            src={item.image_url}
            alt=""
            loading="lazy"
            className="w-full rounded aspect-square object-cover bg-neutral-800"
          />
        </a>
      ) : null}

      <div>
        <p className="font-semibold text-sm">{item.artist}</p>
        {/* `capitalize` rather than a JS title-case pass: a naive title-case
            mangles "USA" to "Usa". CSS only changes rendering,
            so the underlying value stays what the scraper recorded. */}
        <p className="text-sm text-neutral-300 capitalize">{displayTitle(item)}</p>
      </div>

      {meta ? <p className="text-xs text-neutral-500">{meta}</p> : null}
      {dismissedAt ? <p className="text-xs text-neutral-500">Dismissed {dismissedAt}</p> : null}

      {item.url ? (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-xs text-neutral-300 underline hover:text-white"
        >
          View listing
        </a>
      ) : null}

      <button
        onClick={() => onUndismiss(item.key)}
        className="w-full rounded bg-neutral-100 px-3 py-1.5 text-sm text-neutral-900 hover:bg-white"
      >
        Un-dismiss
      </button>
    </li>
  );
}

function Filters({
  filters,
  onChange,
}: {
  filters: DismissedFilters;
  onChange: (next: DismissedFilters) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-md border border-neutral-800 bg-neutral-900 p-3">
      <label className="flex items-center gap-3 text-sm">
        <span className="text-neutral-400">Days back</span>
        <input
          type="range"
          min={DAYS_MIN}
          max={DAYS_MAX}
          step={DAYS_STEP}
          value={filters.days}
          onChange={(e) => onChange({ ...filters, days: Number(e.target.value) })}
          className="accent-neutral-100"
        />
        <span className="w-8 tabular-nums text-neutral-300">{filters.days}</span>
      </label>

      <label className="flex items-center gap-2 text-sm text-neutral-400">
        <input
          type="checkbox"
          checked={filters.onlyAccidental}
          onChange={(e) => onChange({ ...filters, onlyAccidental: e.target.checked })}
          className="accent-neutral-100"
        />
        Only possibly-accidental
      </label>
    </div>
  );
}

export default function Dismissed() {
  const [payload, setPayload] = useState<DismissedPayload | null>(null);
  const [filters, setFilters] = useState<DismissedFilters>(DEFAULT_FILTERS);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    fetchDismissed()
      .then(setPayload)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  useEffect(load, [load]);

  const visible = useMemo(
    () => (payload ? applyFilters(payload.items, filters, new Date()) : []),
    [payload, filters],
  );

  const onUndismiss = useCallback(
    (key: string) => {
      setActionError(null);
      // Optimistic: the item disappears immediately, before the server
      // confirms. On failure it goes back rather than staying gone, so the
      // screen never disagrees with the server about what is dismissed.
      const previous = payload;
      setPayload((current) =>
        current ? { ...current, items: current.items.filter((i) => i.key !== key) } : null,
      );
      undismiss(key).catch((err: unknown) => {
        setPayload(previous);
        setActionError(err instanceof Error ? err.message : String(err));
      });
    },
    [payload],
  );

  if (error) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold">Dismissed</h1>
        <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">{error}</div>
        <button
          onClick={load}
          className="rounded-md bg-neutral-100 px-4 py-2 text-sm text-neutral-900 hover:bg-white"
        >
          Try again
        </button>
      </section>
    );
  }

  if (payload === null) {
    return (
      <section className="space-y-3">
        <h1 className="text-xl font-semibold">Dismissed</h1>
        <p className="text-sm text-neutral-400">Loading…</p>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Dismissed</h1>
        <p className="text-sm text-neutral-400">
          Dismissed listings still live on the source platform. Un-dismiss to bring them back into
          the feed.
        </p>
      </div>

      <Filters filters={filters} onChange={setFilters} />

      {payload.items.length === 0 ? (
        <p className="text-sm text-neutral-400">No still-linkable dismissed items.</p>
      ) : (
        <p className="text-sm text-neutral-400">{summaryLine(visible, payload, filters.days)}</p>
      )}

      {actionError ? (
        <div className="rounded-md border border-amber-900 bg-amber-950/40 p-3 text-sm">
          {actionError}
        </div>
      ) : null}

      {payload.items.length > 0 && visible.length === 0 ? (
        <p className="text-sm text-neutral-400">Nothing matches the current filter.</p>
      ) : null}

      <ul className={CARD_GRID}>
        {visible.map((item) => (
          <Card key={item.key} item={item} onUndismiss={onUndismiss} />
        ))}
      </ul>
    </section>
  );
}
